import { useAnimation } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { MarketContext } from "../../../contexts/MarketContext";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { useMarginRequirements } from "../../../hooks/api/margin/useMarginRequirements";
import { IOptionMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useOrder } from "../../../hooks/api/order/useOrder";
import { useRFQ } from "../../../hooks/api/rfq/useRFQ";
import { useToast } from "../../../hooks/toast";
import usePrevious from "../../../hooks/usePrevious";
import useTickersWSS from "../../../hooks/wss/tickers/useTickersWSS";
import useIndexWSS from "../../../hooks/wss/useIndexWSS";
import useOrderbookWSS from "../../../hooks/wss/useOrderbookWSS";
import usePositionsWSS from "../../../hooks/wss/usePositionsWSS";
import { useFarm } from "../../../hooks/api/farm/useFarm";
import { normalizeDecimalString } from "../../../utils/decimalString";

export function useOptionsTradeFormSetup({
  selectedInstrument,
}: {
  selectedInstrument?: IOptionMarket;
}) {
  const { t: apiError } = useTranslation("apiErrors");
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const { data: farmData, mutate: mutateFarmData } = useFarm();
  const { market } = useContext(MarketContext);
  const { t: commonFormTranslations } = useTranslation("app", {
    keyPrefix: "TradeForm.Common",
  });
  const { t: optionsTradeFormTranslations } = useTranslation("app", {
    keyPrefix: "TradeForm.OptionsTradeForm.OptionsTradeForm",
  });
  const { addToast, addErrorToast } = useToast();
  const { accountSigningKeyState } = useContext(AuthContext);
  const { orderbook: orderbookData } = useOrderbookWSS(
    selectedInstrument?.instrument_name
  );
  const { index } = useIndexWSS(selectedInstrument?.underlying_asset);

  const prevOrderbookInstrument = usePrevious(orderbookData?.instrument_name);
  const animControls = useAnimation();

  const [reduceOnly, setReduceOnly] = useState(false);
  const [orderSide, setOrderSide] = useState<SideResponse>(SideResponse.Buy);
  const [orderType, setOrderType] = useState<OrderTypeResponse>(
    OrderTypeResponse.Market
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (modalRef.current?.clientHeight) {
        setIsSticky(modalRef.current?.clientHeight < 800);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    control,
    trigger,
    reset,
  } = useForm({
    mode: "onChange",
  });
  const amount = useWatch({ control, name: "amount" });
  const price = useWatch({ control, name: "price" });

  const { data: orderData, createOrder } = useOrder();
  const {
    data: accountData,
    showOnboarding,
    isLoading: accountDataLoading,
  } = useGetAccount();
  const { positionsMap } = usePositionsWSS();
  const { deleteRFQsByInstrumentId } = useRFQ(undefined, false);

  const { data: marginData } = useMarginRequirements(
    // Only sell have margin requirements
    orderSide === SideResponse.Sell &&
      amount &&
      selectedInstrument?.instrument_id
      ? {
          is_buy: false,
          // A plain decimal price: the hook scales it to base units itself.
          // `Math.round` sent 0.4 as "0" (no request) and 12.6 as "13".
          limit_price: normalizeDecimalString(price) ?? "0",
          amount,
          instrument_id: Number(selectedInstrument.instrument_id),
        }
      : undefined
  );

  const { instrumentTicker } = useTickersWSS(
    market.asset,
    market.derivative,
    selectedInstrument ? Number(selectedInstrument.expiry) : undefined
  );
  const ticker = selectedInstrument
    ? instrumentTicker[selectedInstrument.instrument_name]
    : undefined;

  // Also trigger updates whenever price is changed
  useEffect(() => {
    if (price && amount) {
      trigger("amount");
      trigger("price");
    }
  }, [price, amount, trigger, marginData]);

  // Update whenever reduce only checkbox is changed
  useEffect(() => {
    if (isDirty) {
      trigger("amount");
    }
  }, [reduceOnly, orderSide, trigger, isDirty, orderType]);

  return {
    selectedInstrument,
    apiError,
    setShowConnectModal,
    farmData,
    mutateFarmData,
    market,
    commonFormTranslations,
    optionsTradeFormTranslations,
    addToast,
    addErrorToast,
    accountSigningKeyState,
    orderbookData,
    index,
    prevOrderbookInstrument,
    animControls,
    reduceOnly,
    setReduceOnly,
    orderSide,
    setOrderSide,
    orderType,
    setOrderType,
    isLoading,
    setIsLoading,
    showCloseModal,
    setShowCloseModal,
    modalRef,
    isSticky,
    register,
    errors,
    handleSubmit,
    setValue,
    reset,
    amount,
    price,
    orderData,
    createOrder,
    accountData,
    showOnboarding,
    accountDataLoading,
    positionsMap,
    deleteRFQsByInstrumentId,
    marginData,
    ticker,
  };
}
